import { getCroppedImg } from "../../../utils/profileImage";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Cropper from "react-easy-crop";
import { Plus, ArrowLeft, X, ZoomIn, ZoomOut, Check } from "lucide-react";
import "./TeamSocialBioPage.css";
import nextIcon from "../../../assets/nextButtonIcon.png";
import { useGlobalAlert } from "../../../context/AlertContext";
import MobileTopBar from "../../../components/MobileTopBar";
import MobileBottomBar from "../../../components/MobileBottomBar";
import DesktopTopBar from "../../../components/DesktopTopBar";
import DesktopBottomBar from "../../../components/DesktopBottomBar";
import progressMobileIconStep2 from "../../../assets/progressIcon5.png";
import progressMobileIconStep1 from "../../../assets/progressIcon4.png";
import { updateUser, updateFcmToken } from "../../../services/userService";
import { updateDogProfile } from "../../../services/dogService";
import { fetchUserData } from "../../../redux/userSlice";



const TeamSocialBioPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { triggerAlert } = useGlobalAlert();

  const [mobileStep, setMobileStep] = useState(location.state?.step || 1);
  const [isLoading, setIsLoading] = useState(false);

  const profile = useSelector((state) => state.user.profile);
  const fcmToken = useSelector((state) => state.user.fcmToken);

  const userId = profile?.id;
  const dogId =
    profile?.dogs && profile.dogs.length > 0 ? profile.dogs[0].id : null;

  const mainImageRef = useRef(null);
  const subImageRefs = useRef([]);

  const [images, setImages] = useState({
    main: null,
    sub1: null,
    sub2: null,
    sub3: null,
  });

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropKey, setCropKey] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const motivationOptions = [
    "I'm between dogs, and want my pup fix",
    "I'm missing my pup back home",
    "My kids are dog-crazy",
    "I love dogs, and just can't have one right now",
    "I would make a great dog aunt or uncle",
  ];

  useEffect(() => {
    const existingMotivations =
      formData.selectedBringsYouHere ||
      formData.whatsBringsYou ||
      formData.brings_you_here ||
      formData.what_brings_you_here;

    if (existingMotivations && Array.isArray(existingMotivations)) {
      const custom = existingMotivations.find(
        (m) => !motivationOptions.includes(m),
      );
      setFormData((prev) => ({
        ...prev,
        selectedBringsYouHere: existingMotivations,
        whatsBringsYou: existingMotivations,
        brings_you_here: existingMotivations,
        customMotivation: custom || prev.customMotivation || "",
      }));
    }

    const photoFiles =
      formData.photos ||
      formData.user_profile_image ||
      profile?.user_profile_image ||
      {};
    const newImages = {};

    if (Array.isArray(photoFiles)) {
      if (photoFiles[0]) newImages.main = photoFiles[0];
      if (photoFiles[1]) newImages.sub1 = photoFiles[1];
      if (photoFiles[2]) newImages.sub2 = photoFiles[2];
      if (photoFiles[3]) newImages.sub3 = photoFiles[3];
    } else {
      Object.entries(photoFiles).forEach(([key, file]) => {
        if (file instanceof File) {
          newImages[key] = URL.createObjectURL(file);
        } else if (typeof file === "string") {
          newImages[key] = file;
        }
      });
    }

    setImages((prev) => {
      Object.values(prev).forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      return {
        main: newImages.main || null,
        sub1: newImages.sub1 || null,
        sub2: newImages.sub2 || null,
        sub3: newImages.sub3 || null,
      };
    });

    return () => {
      Object.values(newImages).forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [formData.photos, profile]);

  const toggleMotivation = (option) => {
    const currentList = formData.selectedBringsYouHere || [];
    const newList = currentList.includes(option)
      ? currentList.filter((item) => item !== option)
      : [...currentList, option];

    setFormData((prev) => ({
      ...prev,
      selectedBringsYouHere: newList,
      whatsBringsYou: newList,
      brings_you_here: newList,
    }));
  };

  const handleCustomMotivation = (value) => {
    const currentList = formData.selectedBringsYouHere || [];
    const oldValue = formData.customMotivation || "";

    let updatedList = currentList.filter((item) => item !== oldValue);
    if (value.trim() !== "") {
      updatedList = [...updatedList, value];
    }

    setFormData((prev) => ({
      ...prev,
      selectedBringsYouHere: updatedList,
      whatsBringsYou: updatedList,
      brings_you_here: updatedList,
      customMotivation: value,
    }));
  };

  const handleImageSelect = (event, key) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setCropImageSrc(reader.result);
        setCropKey(key);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCropModalOpen(true);
      };
      event.target.value = "";
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleSaveCrop = async () => {
    try {
      const croppedFile = await getCroppedImg(
        cropImageSrc,
        croppedAreaPixels,
        cropKey,
      );
      if (croppedFile) {
        setFormData((prev) => ({
          ...prev,
          photos: { ...prev.photos, [cropKey]: croppedFile },
        }));
      }
    } catch (e) {
      console.error("Error generating crop", e);
    } finally {
      setCropModalOpen(false);
      setCropImageSrc(null);
    }
  };

  const handleCancelCrop = () => {
    setCropModalOpen(false);
    setCropImageSrc(null);
  };

  const hasMotivation = (formData.selectedBringsYouHere || []).length > 0;
  const hasAtLeastOneImage = Object.values(images).some((img) => img !== null);
  const isFormValid = hasMotivation && hasAtLeastOneImage;

  const handleCombinedSubmit = async () => {
    if (isLoading) return;

    if (!userId) {
      triggerAlert("User session not found. Please log in again.", "error");
      return;
    }

    setIsLoading(true);

    try {
      const submitData = {
        ...formData,
        user_dog_status: false,
        team_paws: false,
        team_social: true,
        isDogProfileHalfComplete: true,
        isDogProfileComplete: false,
      };

      console.log("Submit Data HalfOnboarding (Social):", submitData);

      await updateUser(userId, submitData, fcmToken);

      if (fcmToken) {
        try {
          await updateFcmToken(userId, fcmToken);
        } catch (fcmError) {
          console.error(
            "Failed to update FCM token during half-onboarding:",
            fcmError,
          );
        }
      }

      if (dogId) {
        await updateDogProfile(dogId, userId, submitData, submitData);
      }

      await dispatch(fetchUserData(userId)).unwrap();
      navigate("/app-tour-page");
    } catch (error) {
      console.error("Update failed:", error);
      triggerAlert(error.message || "Failed to update your profile.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (window.innerWidth >= 1024) {
      if (isFormValid) handleCombinedSubmit();
    } else {
      if (mobileStep === 1) {
        if (hasMotivation) {
          setMobileStep(2);
        }
      } else {
        if (hasAtLeastOneImage) handleCombinedSubmit();
      }
    }
  };

  const handleSkip = () => {
    if (window.innerWidth >= 1024) {
      handleCombinedSubmit();
    } else {
      if (mobileStep === 1) {
        setMobileStep(2);
      } else {
        handleCombinedSubmit();
      }
    }
  };

  const handleMobileBack = () => {
    if (mobileStep === 1) {
      navigate("/team-social-dimsum-availability");
    } else {
      setMobileStep(1);
    }
  };

  return (
    <div
      className={`tsbio-container ${mobileStep === 1 ? "tsbio-mobile-step-1" : "tsbio-mobile-step-2"}`}
    >
      {/* LEFT PANEL (Motivations - Step 1) */}
      <div
        className={`tsbio-panel tsbio-left-panel ${mobileStep === 1 ? "tsbio-mobile-active" : "tsbio-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={handleMobileBack}
          backColor="#fff"
          logoColor="#fff"
          isProtectedRoute={false}
        />

        <DesktopTopBar
          showBack={true}
          onBack={() => navigate("/team-social-dimsum-availability")}
          backColor="#000"
          showProgress={false}
        />

        <div className="tsbio-content-wrapper">
          <div className="tsbio-header-section-step-1 tsp-mobile-only">
            <div className="tsbio-dog-icon-container-step-1">
              <img
                src={progressMobileIconStep1}
                alt="Progress Step"
                className="tsp-progress-icon"
              />
            </div>
            <div className="tsbio-progress-track">
              <div className="tsbio-progress-fill tsbio-step-1-fill"></div>
            </div>
          </div>
          <div className="tsbio-title-container">
            <h1 className="tsbio-title">WHAT BRINGS YOU HERE?</h1>
          </div>
          <p className="tsbio-subtitle">
            Pup Pals join Social Paws because they love dogs but don’t have one
            in their lives right now. Maybe you’re missing your dog back home,
            recently lost a pup, or just aren’t in a place to have one of your
            own. We’d love to know your story—choose what fits or share more in
            “Write your own!”
          </p>

          <div className="tsbio-list">
            {motivationOptions.map((option, index) => {
              const isChecked = (formData.selectedBringsYouHere || []).includes(
                option,
              );
              return (
                <div
                  key={index}
                  className="tsbio-motivation-row"
                  onClick={() => toggleMotivation(option)}
                >
                  <div className="tsbio-text-col">
                    <span className="tsbio-motivation-label">{option}</span>
                    <div className="tsbio-underline-light"></div>
                  </div>
                  <div
                    className={`tsbio-checkbox ${isChecked ? "checked" : ""}`}
                  >
                    {isChecked && (
                      <Check size={20} strokeWidth={4} color="#000" />
                    )}
                  </div>
                </div>
              );
            })}

            <div
              className="tsbio-write-box tsbio-bg-teal"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingRight: "15px",
              }}
            >
              <div style={{ flex: 1, marginRight: 0 }}>
                <input
                  type="text"
                  placeholder="Write your own"
                  className="tsbio-write-input"
                  style={{ width: "100%" }}
                  value={formData.customMotivation || ""}
                  onChange={(e) => handleCustomMotivation(e.target.value)}
                />
              </div>
              {formData.customMotivation?.trim() && (
                <div
                  className="tsbio-checkbox checked"
                  style={{ marginLeft: "6px" }}
                >
                  <Check size={20} strokeWidth={4} color="#000" />
                </div>
              )}
            </div>
          </div>

          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={hasMotivation}
            nextIconSrc={nextIcon}
            showSkip={true}
            onSkip={handleSkip}
            variant="green"
          />
        </div>
      </div>

      {/* RIGHT PANEL (Photos - Step 2) */}
      <div
        className={`tsbio-panel tsbio-right-panel ${mobileStep === 2 ? "tsbio-mobile-active" : "tsbio-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={handleMobileBack}
          backColor="#fff"
          logoColor="#fff"
          isProtectedRoute={false}
        />

        <div className="tsbio-content-wrapper tsbio-right-content">
          <div className="tsbio-header-section-step-2 tsp-mobile-only">
            <div className="tsbio-dog-icon-container-step-2">
              <img
                src={progressMobileIconStep2}
                alt="Progress Step"
                className="tsp-progress-icon"
              />
            </div>
            <div className="tsbio-progress-track">
              <div className="tsbio-progress-fill tsbio-step-2-fill"></div>
            </div>
          </div>
          <div className="tsbio-header-group">
            <h1 className="tsbio-title tsbio-text-white">YOUR PICS</h1>
            <p className="tsbio-subtitle tsbio-text-white">
              A happy headshot, a pic showcasing the dog-lover in you, and pics
              showing off what you do in the world, are all great choices!
            </p>
          </div>

          {/* PHOTO GRID */}
          <div className="tsbio-photo-grid">
            <div
              className="tsbio-photo-box tsbio-main-photo"
              onClick={() => mainImageRef.current.click()}
            >
              <input
                type="file"
                hidden
                ref={mainImageRef}
                accept="image/*"
                onChange={(e) => handleImageSelect(e, "main")}
              />
              {images.main ? (
                <img
                  src={images.main}
                  alt="Main Upload"
                  className="tsbio-uploaded-img"
                />
              ) : (
                <Plus size={40} color="#000" />
              )}
            </div>

            <div className="tsbio-sub-photos-row">
              {["sub1", "sub2", "sub3"].map((key, index) => (
                <div
                  key={key}
                  className="tsbio-photo-box tsbio-sub-photo"
                  onClick={() => subImageRefs.current[index].click()}
                >
                  <input
                    type="file"
                    hidden
                    ref={(el) => (subImageRefs.current[index] = el)}
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, key)}
                  />
                  {images[key] ? (
                    <img
                      src={images[key]}
                      alt="Sub Upload"
                      className="tsbio-uploaded-img"
                    />
                  ) : (
                    <Plus size={32} color="#000" />
                  )}
                </div>
              ))}
            </div>
            <div className="tsbio-header-group">
              <p
                className="tsbio-subtitle tsbio-text-white"
                style={{ marginBottom: "0" }}
              >
                We require at least one photo to help us create quality
                profiles. You can always come back to add more photos or update
                your profile anytime!
              </p>
            </div>
          </div>

          <DesktopBottomBar
            onNext={handleNext}
            isNextEnabled={isFormValid}
            nextIconSrc={nextIcon}
            showSkip={false}
            isLoading={isLoading}
            variant="green"
          />

          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={hasAtLeastOneImage}
            nextIconSrc={nextIcon}
            showSkip={false}
            isLoading={isLoading}
            variant="green"
          />
        </div>
      </div>

      {cropModalOpen && cropImageSrc && (
        <div className="crop-modal-overlay">
          <div className="crop-modal-box">
            <button className="crop-close-btn" onClick={handleCancelCrop}>
              <X size={20} color="rgba(166, 28, 31, 1)" strokeWidth={3} />
            </button>

            <h2 className="crop-modal-title">Upload Picture</h2>

            <div className="crop-container">
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                classes={{
                  containerClassName: "custom-cropper-container",
                }}
                cropAreaStyle={{
                  border: "2px dashed white",
                }}
              />
            </div>

            <div className="crop-slider-wrapper">
              <ZoomOut size={24} color="#000" />
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e) => setZoom(e.target.value)}
                className="crop-slider"
              />
              <ZoomIn size={24} color="#000" />
            </div>

            <div className="crop-action-row">
              <button
                className="crop-btn-change"
                onClick={() => {
                  if (cropKey === "main") {
                    mainImageRef.current.click();
                  } else {
                    const index = parseInt(cropKey.replace("sub", "")) - 1;
                    subImageRefs.current[index].click();
                  }
                }}
              >
                CHANGE IMAGE
              </button>
              <button className="crop-btn-save" onClick={handleSaveCrop}>
                CROP & SAVE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSocialBioPage;
